import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  Button,
  CheckBox,
  DropdownMenu,
  EditableChip,
  Horizontal3DotsIcon,
  InfoIcon,
  Input,
  KbFolderDualIcon,
} from "components";
import {
  useDeleteKbRecords,
  useGetKbFolderContents,
  useMoveKbRecords,
  useNonPersistantMainStore,
  usePermissionStore,
  useToggleKbRecordsStarredStatus,
  useUpdateKbFolder,
} from "controller";
import { useState, useRef, useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import {
  selectedKbItemIdsArr,
  toggleKbSelectionFn,
  recentlyCreatedFolderId,
  clearRecentlyCreatedFolderId,
} from "../store";
import { fileActionOptions } from "../constants";
import { KbFileListDrawer } from "./KbFileListDrawer";
import toast from "react-hot-toast";
import { KbThreeDots } from "./KbThreeDots";
import { PERMISSIONS } from "shared";

export const KbFolderCard = ({
  folderDetails,
}: // isRenaming,
{
  folderDetails: any;
  // isRenaming: boolean;
}) => {
  const chipRef = useRef();
  const theme = useTheme();
  const navigate = useNavigate();
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.KB.KB_KB_RW]),
  );
  // const { data: folderContents } = useGetKbFolderContents({
  //   folderId: folderDetails?.mapping_id,
  // });

  const { mutateAsync: updateKbFolder } = useUpdateKbFolder();
  const { mutateAsync: moveKbRecords } = useMoveKbRecords();
  const { mutateAsync: deleteKbRecords } = useDeleteKbRecords();
  const { mutateAsync: toggleKbRecordsStarredStatus } =
    useToggleKbRecordsStarredStatus();

  const selectedKbItemIds = useAtomValue(selectedKbItemIdsArr);
  const toggleSelection = useSetAtom(toggleKbSelectionFn);
  const isSelected =
    selectedKbItemIds?.includes(folderDetails?.mapping_id) || false;

  const recentlyCreatedId = useAtomValue(recentlyCreatedFolderId);
  const clearRecentlyCreated = useSetAtom(clearRecentlyCreatedFolderId);
  const isRecentlyCreated = recentlyCreatedId === folderDetails?.mapping_id;

  // Auto-trigger edit mode for newly created folder
  useEffect(() => {
    if (
      isRecentlyCreated &&
      chipRef.current &&
      !folderDetails?.is_customer_folder &&
      folderDetails.parent_id
    ) {
      // Delay slightly to ensure the component renders with proper sorting first
      const timer = setTimeout(() => {
        // @ts-ignore
        chipRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    isRecentlyCreated,
    folderDetails?.is_customer_folder,
    folderDetails.parent_id,
  ]);

  const splitFullname = folderDetails ? folderDetails?.name?.split(" ") : [];
  const handleNameChange = async (name: string) => {
    try {
      await updateKbFolder({
        folderId: folderDetails?.mapping_id,
        folderData: {
          name,
        },
      });
      toast.success("Folder renamed successfully");

      // Clear recently created state after user finishes editing
      if (isRecentlyCreated) {
        clearRecentlyCreated();
      }
    } catch (error) {
      toast.error("Failed to rename folder");
    }
  };

  const handleMoveRecords = async (item: string[]) => {
    try {
      await moveKbRecords({
        parent_id: item[0],
        record_ids: [folderDetails?.mapping_id],
      });
      toast.success("Folder moved successfully");
    } catch (error) {
      toast.error("Failed to move folder");
    }
  };

  const handleSingleClick = () => {
    if (clickTimeoutRef.current) {
      return;
    }

    clickTimeoutRef.current = setTimeout(() => {
      if (folderDetails?.is_customer_folder || !folderDetails.parent_id) {
        toast.custom(
          <Stack
            sx={{
              p: "10px 16px",
              backgroundColor: theme.palette.lightBg.medium,
              borderRadius: "8px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <InfoIcon sx={{ color: "#FFF" }} />
            <Typography variant="body-medium" color={"#FFF"}>
              Cannot select default folder
            </Typography>
          </Stack>,
          { position: "bottom-center", duration: 2000 },
        );
      } else {
        toggleSelection(folderDetails?.mapping_id);
      }
      clickTimeoutRef.current = null;
    }, 200);
  };

  const handleDoubleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    navigate(`/app/kb/folder/${folderDetails?.mapping_id}`);
  };
  return (
    <Stack
      sx={{
        width: "248px",
        height: "64px",

        // aspectRatio: "3.83",
        p: "12px 16px",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: "8px",
        border: `1px solid ${
          isSelected ? theme.palette.primary.main : theme.palette.neutral["010"]
        }`,
        borderRadius: "8px",
        boxShadow:
          "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
        cursor: "pointer",
        "&:hover": {
          boxShadow: "-4px 9px 11px 0px rgba(0, 0, 0, 0.08)",
        },
        bgcolor: isSelected
          ? theme.palette.secondary["purple-tint-3"]
          : "transparent",

        "& .checkbox": {
          display: isSelected ? "block" : "none",
        },
        "& .three-dots": {
          display: isSelected ? "block" : "none",
        },
        ":hover": {
          // gap: "2px",
          "& .checkbox": {
            display: "block",
          },
          "& .three-dots": {
            display: "block",
          },
        },
      }}
      // onDoubleClick={handleDoubleClick}
      onClick={handleDoubleClick}
    >
      <Stack
        sx={{
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            position: "absolute",
            top: "12px",
          }}
          variant="caption-xs"
        >
          {splitFullname?.length > 0
            ? splitFullname[0].charAt(0).toUpperCase()
            : ""}
          {splitFullname?.length > 1
            ? splitFullname[1].charAt(0).toUpperCase()
            : ""}
        </Typography>
        <KbFolderDualIcon
          color="primary"
          style={{
            width: "32px",
            height: "32px",
          }}
        />
      </Stack>

      <Stack gap="0px" sx={{ width: "112px" }}>
        <Box
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <EditableChip
            ref={chipRef}
            disabled={
              isSelected ||
              folderDetails?.is_customer_folder ||
              !folderDetails.parent_id
                ? true
                : false
            }
            value={folderDetails?.name}
            onEdit={handleNameChange}
            inputSx={{
              ...theme.typography["body-small"],
              px: 0,
            }}
            sx={{
              // width: "80px",
              maxWidth: "140px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...theme.typography["caption-2"],
            }}
            typographySx={{
              px: 0,
              ...theme.typography["caption-2"],
            }}
            onNavigate={handleDoubleClick}
          />
        </Box>

        {/* <Typography variant="body-small">{folderDetails?.name}</Typography> */}
        <Typography
          sx={{ width: "fit-content" }}
          variant="body-xsmall"
          color={theme.palette.lightBg.low}
        >
          {folderDetails?.children?.length} Items
        </Typography>
      </Stack>
      {haveFullAccess && (
        <Box className="three-dots">
          {!isSelected && (
            <KbThreeDots
              fileDetails={folderDetails}
              isSelected={isSelected}
              isDefaultFolder={
                folderDetails?.is_customer_folder || !folderDetails.parent_id
                  ? true
                  : false
              }
              type="folder"
              onActionClick={(type) => {
                if (type === "rename") {
                  // @ts-ignore
                  chipRef?.current?.focus();
                }
              }}
            />
          )}
        </Box>
      )}
      {haveFullAccess && (
        <Box
          ml={"auto"}
          className="checkbox"
          onClick={(e) => e.stopPropagation()}
        >
          <CheckBox checked={isSelected} onClick={handleSingleClick} />
        </Box>
      )}
    </Stack>
  );
};
